// @flow

/** @jsx jsx */

import React from 'react';
import { GroupRow } from 'wijmo/wijmo.grid'
import { FlexGridFilter } from 'wijmo/wijmo.react.grid.filter';
import { PropertyGroupDescription } from 'wijmo/wijmo';
import { FlexGrid } from 'wijmo/wijmo.react.grid';
import { GroupPanel } from 'wijmo/wijmo.react.grid.grouppanel';
import * as wjcCore from 'wijmo/wijmo'
import * as wjGrid from 'wijmo/wijmo.grid'
import { jsx } from '@emotion/core';
import type { ColumnsMap, FormatterArgs } from '../types';
import { parseField, populateSortDescriptions } from '../utils'
import { FreezeBar } from '../controls/FreezeBar';
import Container from './Container';
import BlockingStatus from './BlockingStatus';
import messages from '../config/messages';
import Loading from './Loading'
import { ColumMap, GroupPanelsDefinition } from '../types'
import BlockingLoadingStatus from './BlockingLoadingStatus'
import PerfTimer from '../utils/perfTimer'
import { getDateFilters, DATE_OPTIONS } from './actions/DateFilterAction'
import { FILTER_VALUES_LIMIT } from './GridApi';
import DragDropOverlay from './DragDropOverlay';

type Props = {
  grid: FlexGrid,
  collection: any,
  columnsMapping?: ColumnsMap,
  formatters?: (formattersArgs: FormatterArgs) => Array<any>,
  frozenColumns?: number,
  hasAllColumnsHidden?: boolean,
  onGridInitialized: FlexGrid => void,
  onGroupPanelChanged?: (groups: GroupPanelsDefinition) => mixed,
  onColSorted: () => mixed,
  onColResized: () => mixed,
  onColDragged: () => mixed,
  onColFreezed: () => mixed,
  onRowFreezed: () => mixed,
  onClearSelection: () => mixed,
  onRowSelected: any => mixed,
  showFrozenBars?: boolean,
  showGroupPanel?: boolean,
  groups: Array<string>,
  showTotalRow?: boolean,
  customAggregation?: () => mixed,
  exportProgress: number | null,
};

const INITIAL_LOADING_STATUS = ['', 'initial-mount', 'initial-loading'];
const EXPORTING_STATUS = ['exporting', 'exporting-loaded'];

export default class GridFlask extends React.PureComponent<Props> {
  static defaultProps = {
    collection: null,
    columnsMapping: [],
    showFrozenBars: false,
    showGroupPanel: false,
    showTotalRow: true,
    multiColumnSort: false
  };

  state = {
    initialGridLoading: true,
    loadingGroups: true,
    disabledInertialScrolling: false,
  };

  constructor(props) {
    super(props);
    this.containerRef = React.createRef();
    this.flexGridRef = React.createRef()
    this.gridFilter = null;

    if (props.collection) {
      props.collection.pageChanged.addHandler(() => {
        this.removeSelection();

        this.props.onClearSelection();
      });

      props.collection.jsonReviver = (key, value) => parseField(key, value);
    }
  }

  /**
   * This will activate (when needed) the Grouping capabilities of
   * FlexGrid
   * @see https://www.grapecity.com/wijmo/demos/Grid/Grouping/Groups/react
   */
  connectGroupPanelWithGrid = (grid: FlexGrid) => {
    const panel = wjcCore.Control.getControl(
      document.getElementById(`panel-${this.id}`),
    );

    if (panel) {
      panel.addEventListener(
        panel.hostElement,
        'drop',
        this.handleGroupPanelChanged.bind(this),
      );

      panel.addEventListener(
        panel.hostElement,
        'click',
        this.handleGroupPanelChanged.bind(this),
      );

      this.panel = panel;
      this.panelGroupInitialized = true;
    }
  };

  /**
   * Formatters are the functions used for colorizing the cells/rows
   */
  cleanAndInitFormatters = (formatters: any, args?: FormatterArgs) => {
    if (typeof formatters === 'function') {
      this.formatters = formatters(args);

      this.formatters.forEach(f => {
        this.grid.formatItem.addHandler(f);
      });
    }
  };

  /**
   * Will remove the selection from the grid. By default the first row
   * is selected
   */
  removeSelection = () => {
    if (!this.grid || typeof this.grid.select !== 'function') return;

    this.grid.select(-1, -1);
  };

  getColumnsProps = (columnsMapping?: ColumnsMap) => {
    if (
      columnsMapping &&
      columnsMapping.length > 0 &&
      !this.props.hasAllColumnsHidden
    ) {
      return {
        columns: columnsMapping,
      };
    }

    return {};
  };

  /**
   * Should be called once the moment the collection finished fetching.
   */
  initializeGrid = () => {
    this.grid.scrollPosition = 0;
    if (this.props.showGroupPanel && this.panelGroupInitialized !== true) {
      this.connectGroupPanelWithGrid(this.grid);
    }


    if (typeof this.props.formatters === 'function') {
      this.cleanAndInitFormatters(
        this.props.formatters,
        this.props.formattersArgs,
      );
    }

    // First row is selected by default so we'll remove this behavior.
    this.removeSelection();

    // reset scroll's position (to start at the top)
    this.grid.scrollPosition = 0;

    if (this.props.showFrozenBars) {
      this.freezeBarCol = new FreezeBar({
        onColFreezed: this.props.onColFreezed,
        frozenColumns: this.props.frozenColumns,
      });
      this.freezeBarCol.addFreezeBar(this.grid);
    }
  };

  componentDidMount() {
    this.props.isMobile && document.addEventListener('touchstart', this.menuHandler, true);
  }
  componentWillUnmount() {
    this.props.isMobile && document.removeEventListener('touchstart', this.menuHandler, true);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.status === 'loaded' && this.state.initialGridLoading) {
      this.initializeGrid();
      this.setState({ initialGridLoading: false });
    }

    if (
      this.props.frozenColumns !== prevProps.frozenColumns &&
      this.freezeBarCol &&
      typeof this.freezeBarCol.resetFreezeBar === 'function'
    ) {
      this.freezeBarCol.resetFreezeBar(this.props.frozenColumns);
    }

    // This is to fix the issue where sometimes the Grid only shows
    // the top-left part of the whole grid
    if (
      !prevProps.active &&
      this.props.active &&
      this.grid &&
      typeof this.grid.refresh === 'function'
    ) {
      this.grid.refresh();
    }
    if (this.props.isMobile && !this.state.disabledInertialScrolling && this.flexGridRef && this.flexGridRef.current) {
      this.disableInertialScrolling(this.flexGridRef.current.control);
      this.setState({ disabledInertialScrolling: true });
    }
  }

  onGridInitialized = (grid: FlexGrid) => {
    if (this.props.showTotalRow) {
      grid.columnFooters.rows.push(new GroupRow());
      grid.bottomLeftCells.setCellData(0, 0, 'Σ');
    }

    this.grid = grid;
    this.addGroupDescriptions();

    if (this.props.customAggregation) this.props.customAggregation(grid, wjGrid, wjcCore)

    this.props.onGridInitialized(grid);
    this.gridFilter && this.changeMaxFilterValue(grid);
  };

  changeMaxFilterValue = (grid) => {
    for (var c = 0; c < grid.columns.length; c++) {
      this.gridFilter.getColumnFilter(c).valueFilter.maxValues = FILTER_VALUES_LIMIT;
    }
  };
  // this is the last function to complete, when we are initially launching the grid
  // so we have some additional logic (other then groups) in the body of this function
  addGroupDescriptions() {
    if (this.grid) {
      while(this.grid.collectionView.groupDescriptions.length) {
        this.grid.collectionView.groupDescriptions.pop()
      }

      if (this.props.groups) {
        this.props.groups.forEach(group => {
          const groupDescription = new PropertyGroupDescription(group)
          this.grid.collectionView.groupDescriptions.push(groupDescription)
        })
      }

      // to select on Invoices and Samples MTD filter right after complete initialize of the grid
      // and for Invoices we call default filtering for Rep Accounts additionally to MTD
      if (this.props.autoMTD) {
        if (this.props.isRep) {
          this.props.onFiltersChanged("Rep", {
            'AcctR': {
              'eq': this.props.repCode
            }
          })
        }

        this.props.onFiltersChanged("Date", getDateFilters()[DATE_OPTIONS[0].value])
      }

      setTimeout(() => {
        PerfTimer.getInstance().print('lifted curtain')
        this.setState({loadingGroups: false})
      }, 100)
    }
  }

  setGridFilter = (filter) => {
    this.gridFilter = filter;
    this.props.setFlexGridFilter(filter);
  };

  handleGroupPanelChanged = () => {
    this.grid.select(-1, -1)

    const groupDesc = this.grid.collectionView.groupDescriptions
    const result: GroupPanelsDefinition = []
    // TODO: decide if we need groups
    //     const g = this.collection.groups
    groupDesc.forEach(item => {
      result.push(item.propertyName)
    })

    this.props.onGroupPanelChanged(result)
  }

  // if not tapping on grid or menu, it will clear the row selection and close the menu
  menuHandler = (e) => {
    if (this.props.isMobile
      && this.props.menuRef
      && this.props.currentItem
      && this.props.menuRef.current
      && !this.props.menuRef.current.contains(e.target) //checks if the menu is not being tapped on
      && !this.containerRef.current.contains(e.target) //checks that the grid is not being tapped on
    ) {
      const modal = document.getElementsByClassName('modal')[0]
      const modalOpen = modal && modal.classList.contains('in')
      if (!modalOpen) {
        this.removeSelection();
        this.props.onClearSelection();
        this.props.onCurrentItemChanged(null);
      }
    }
  };

  disableInertialScrolling = (grid) => {
    let root = grid._root,
      sp = null,
      start = null;
    root.addEventListener('touchstart', e => {
      sp = grid.scrollPosition;
      start = e;
    });
    root.addEventListener('touchmove', e => {
      grid.scrollPosition = new wjcCore.Point(
        sp.x + (e.pageX - start.pageX),
        sp.y + (e.pageY - start.pageY),
      );
      e.preventDefault();
      e.stopPropagation();
    });
  }

  render() {
    const { collection: items, playResetAnimation } = this.props

    const isInitialLoad = INITIAL_LOADING_STATUS.includes(this.props.status);
    const isLoading = this.state.loadingGroups || [...INITIAL_LOADING_STATUS, 'loading'].includes(this.props.status) || playResetAnimation;
    const isExporting = EXPORTING_STATUS.includes(this.props.status);
    const hasNoContentToDisplay =
      this.props.hasAllColumnsHidden ||
      (!this.props.hasDataToDisplay && this.props.status === 'loaded');

    const columns = this.props.columnsMapping
    const hasColumns = columns && columns.length > 0

    const multiColumnSort = this.props.multiColumnSort;

    const exportProgressMessage = this.props.exportProgress !== null
      ? `${messages.common.exporting} ${this.props.exportProgress}%`
      : messages.common.exporting;

    return (
      <React.Fragment>
        <BlockingStatus
          active={isExporting}
          container={document.body}
          text={exportProgressMessage}
        />
        <BlockingLoadingStatus
          active={isLoading}
          container={document.body}
          text={<Loading />}
          excludeHeader
        />
        <DragDropOverlay>{({draggable, isMobile}) => {
          return (
            <Container ref={this.containerRef} isFullPage direction="column">
              {!isInitialLoad && (
                <React.Fragment>
                  {this.props.showGroupPanel && (
                    <div
                      css={{
                        width: '100%',
                      }}
                    >
                      <GroupPanel
                        id={`panel-${this.id}`}
                        placeholder="Drag columns here to create Groups"
                        grid={this.grid}
                        hideGroupedColumns={false}
                      />
                    </div>
                  )}
                  {hasNoContentToDisplay && (
                    <Container
                      css={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 20,
                        pointerEvents: 'none',
                      }}
                    >
                    </Container>
                  )}
                  {hasColumns && (
                    <FlexGrid
                      allowSorting={multiColumnSort ? "MultiColumn" : "SingleColumn"}
                      ref={this.flexGridRef}
                      itemsSource={items}
                      isReadOnly={true}
                      autoGenerateColumns={false}
                      initialized={this.onGridInitialized}
                      allowDragging={isMobile ? draggable : true}
                      onSortedColumn={() =>
                        this.handleSortedColumn()
                      }
                      onResizedColumn={() =>
                        this.handleResizedColumn()
                      }
                      onDraggedColumn={() =>
                        this.handleDraggedColumn()
                      }
                      onSelectionChanged={e => {
                        // e.col === 0 only when there is no user interaction involved.
                        // so this is a guard to avoid unnecessary updates
                        if (e.col >= 0 && e.row >= 0) {
                          this.props.onRowSelected(this.grid)
                        }
                      }}
                      onUpdatedView={() => {
                        // PerfTimer.getInstance().print('view updated')
                      }}
                      onUpdatedLayout={() => {
                        // PerfTimer.getInstance().print('layout updated')
                      }}
                      onRefreshed={() => {
                        // PerfTimer.getInstance().print('grid refreshed')
                      }}
                      columns={columns}
                    >
                      <FlexGridFilter
                        filterApplied={this.handleInternalFilter.bind(this)}
                        initialized={this.setGridFilter}
                      />
                    </FlexGrid>
                  )}

                </React.Fragment>
              )}
            </Container>
          )
        }}
        </DragDropOverlay>

      </React.Fragment>
    );
  }

  handleInternalFilter(passedFilter) {
    if (this.flexGridRef.current) {
      const flexGrid = this.flexGridRef.current.control;
      this.props.onInternalGridFiltersChanged(flexGrid, passedFilter)
    }
  }

  // Notify the listeners up-the-stack,
  // perhaps the controller
  handleSortedColumn() {
    if (this.props.onColSorted) {
      this.props.onColSorted(populateSortDescriptions(this.grid.collectionView))
    }
  }

  handleResizedColumn() {
    const columns = this.grid.columns
    const result: ColumnsMap = []
    columns.forEach(item => {
      const c: ColumMap = {
        binding: item.binding,
        header: item.header,
        visible: item.isVisible,
        size: item.size,
      }
      result.push(c)
    })

    if (this.props.onColResized) {
      this.props.onColResized(result)
    }
  }

  handleDraggedColumn() {
    const columns = this.grid.columns
    const result: ColumnsMap = []
    columns.forEach(item => {
      const c: ColumMap = {
        binding: item.binding,
        header: item.header,
        visible: item.isVisible,
      }
      result.push(c)
    })

    if (this.props.onColDragged) {
      this.props.onColDragged(result)
    }
  }
}
